import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getUrl, uploadData, remove } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { captureError } from '../../../../utils/sentryHelpers';

const client = generateClient();

/**
 * Hook for managing lecture file operations (upload, download, delete)
 * Handles PDF documents (German/English) and presentations
 */
export const useLectureFileManager = () => {
  const [isUploading, setIsUploading] = useState({
    De: false,
    En: false,
    Presentation: false
  });
  
  const [uploadErrors, setUploadErrors] = useState({
    De: null,
    En: null,
    Presentation: null
  });

  /**
   * Upload a file to S3 and update the lecture record
   */
  const uploadFile = useCallback(async (file, fileType, lectureId) => {
    if (!file || !lectureId) {
      throw new Error('File and lecture ID are required');
    }
    
    try {
      setIsUploading(prev => ({ ...prev, [fileType]: true }));
      setUploadErrors(prev => ({ ...prev, [fileType]: null }));
      
      // Upload file to S3
      const result = await uploadData({
        key: file.name,
        data: file,
        options: {
          accessLevel: 'protected'
        }
      }).result;
      
      // Get identity ID for S3 path
      const identityId = (await fetchAuthSession()).identityId;
      
      // Prepare update field based on file type
      let updateField = {};
      if (fileType === 'De' || fileType === 'En') {
        updateField[`pdf${fileType}`] = {
          fileName: result.key,
          name: result.key,
          s3Path: `protected/${identityId}/${result.key}`,
          updatedAt: new Date().toISOString()
        };
      } else if (fileType === 'Presentation') {
        updateField.presentation = {
          fileName: result.key,
          name: result.key,
          s3Path: `protected/${identityId}/${result.key}`,
          updatedAt: new Date().toISOString()
        };
      }
      
      // Update lecture in database
      const updatedLecture = await client.graphql({
        variables: {
          input: {
            id: lectureId,
            ...updateField
          }
        },
        query: /* GraphQL */ `
          mutation UpdateLecture($input: UpdateLectureInput!) {
            updateLecture(input: $input) {
              id
              pdfDe {
                fileName
                name
                s3Path
                updatedAt
              }
              pdfEn {
                fileName
                name
                s3Path
                updatedAt
              }
              presentation {
                fileName
                name
                s3Path
                updatedAt
              }
            }
          }
        `
      });
      
      setIsUploading(prev => ({ ...prev, [fileType]: false }));
      
      return {
        success: true,
        data: updatedLecture.data.updateLecture,
        fileInfo: fileType === 'De' || fileType === 'En' 
          ? updatedLecture.data.updateLecture[`pdf${fileType}`]
          : updatedLecture.data.updateLecture.presentation
      };

    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'upload-lecture-file',
        consoleMessage: 'Error uploading file:',
        context: { fileType, lectureId, fileName: file?.name }
      });
      setIsUploading(prev => ({ ...prev, [fileType]: false }));
      setUploadErrors(prev => ({
        ...prev,
        [fileType]: error.errors?.[0]?.message || error.message || 'Upload failed'
      }));
      throw error;
    }
  }, []);

  /**
   * Download a file by getting a signed URL
   */
  const downloadFile = useCallback(async (fileName, s3Path) => {
    if (!fileName || !s3Path) {
      throw new Error('File name and S3 path are required');
    }
    
    try {
      // Extract identity ID from S3 path
      const regex = /protected\/(eu-central-1:[a-z0-9-]+)\//i;
      const match = s3Path.match(regex);
      
      if (!match) {
        throw new Error('Invalid S3 path format');
      }
      
      const identity = match[1];
      
      const getUrlResult = await getUrl({
        key: fileName,
        options: {
          accessLevel: 'protected',
          targetIdentityId: identity,
        },
      });
      
      // Open file in new tab
      window.open(getUrlResult.url, '_blank');
      
      return {
        success: true,
        url: getUrlResult.url
      };

    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'download-lecture-file',
        consoleMessage: 'Error downloading file:',
        context: { fileName, s3Path }
      });
      throw error;
    }
  }, []);

  /**
   * Remove a file from S3 and update the lecture record
   */
  const removeFile = useCallback(async (fileName, fileType, lectureId) => {
    if (!fileName || !lectureId) {
      throw new Error('File name and lecture ID are required');
    }
    
    try {
      // Remove file from S3
      await remove({ 
        key: fileName, 
        options: { accessLevel: 'protected' } 
      });
      
      // Prepare update field to clear the file reference
      let updateField = {};
      if (fileType === 'De' || fileType === 'En') {
        updateField[`pdf${fileType}`] = null;
      } else if (fileType === 'Presentation') {
        updateField.presentation = null;
      }
      
      // Update lecture in database
      const updatedLecture = await client.graphql({
        variables: {
          input: {
            id: lectureId,
            ...updateField
          }
        },
        query: /* GraphQL */ `
          mutation UpdateLecture($input: UpdateLectureInput!) {
            updateLecture(input: $input) {
              id
              pdfDe { fileName name s3Path }
              pdfEn { fileName name s3Path }
              presentation { fileName name s3Path }
            }
          }
        `
      });
      
      return {
        success: true,
        data: updatedLecture.data.updateLecture
      };

    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'remove-lecture-file',
        consoleMessage: 'Error removing file:',
        context: { fileName, fileType, lectureId }
      });
      throw error;
    }
  }, []);

  /**
   * Clear upload errors for a specific file type
   */
  const clearUploadError = useCallback((fileType) => {
    setUploadErrors(prev => ({ ...prev, [fileType]: null }));
  }, []);

  /**
   * Clear all upload errors
   */
  const clearAllUploadErrors = useCallback(() => {
    setUploadErrors({ De: null, En: null, Presentation: null });
  }, []);

  return {
    // State
    isUploading,
    uploadErrors,
    
    // Actions
    uploadFile,
    downloadFile,
    removeFile,
    
    // Error management
    clearUploadError,
    clearAllUploadErrors
  };
};